<template>
  <CCard v-if="destinos.length">
    <CCardHeader>Aplicar também a outras finalidades</CCardHeader>

    <CCardBody>
      <p class="explicacao">
        Copia o que está salvo aqui para as finalidades marcadas, credenciais
        incluídas. Use quando todas saem pelo mesmo servidor — assim uma troca de
        senha não precisa ser repetida uma a uma.
      </p>

      <ul class="destinos">
        <li v-for="destino in destinos" :key="destino.chave" class="destino">
          <label class="destino__rotulo">
            <input v-model="selecionados" type="checkbox" :value="destino.chave" />
            <span>
              {{ destino.nome }}
              <small v-if="destino.configurada" class="destino__aviso">
                já configurada — será sobrescrita
              </small>
              <small v-else class="destino__novo">ainda não configurada</small>
            </span>
          </label>
        </li>
      </ul>

      <div class="acoes">
        <CButton
          variant="outline"
          color="secondary"
          size="sm"
          :disabled="!destinos.length"
          @click="alternarTodos"
        >
          {{ todosMarcados ? 'Limpar seleção' : 'Marcar todas' }}
        </CButton>

        <CButton
          color="info"
          size="sm"
          :disabled="!selecionados.length || replicando"
          @click="replicar"
        >
          {{ replicando ? 'Aplicando...' : rotuloAplicar }}
        </CButton>
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
/*
 * Copia a configuração desta instância para outras finalidades do conector.
 *
 * O SMTP tem oito finalidades e, na maior parte dos clientes, todas apontam
 * para o mesmo servidor: sem isto, são nove campos preenchidos oito vezes — e
 * cada troca de senha repete o trabalho, com o risco de esquecer uma finalidade
 * e só descobrir quando um e-mail deixar de chegar.
 *
 * Só aparece na edição de conector multi-instância com escopos declarados: para
 * os demais não há outra finalidade para onde copiar.
 */
import { post } from '@/helpers/apiRequest';

export default {
  name: 'ReplicarConfiguracao',
  props: {
    integracaoId: {
      type: Number,
      required: true,
    },
    // Escopos declarados no manifesto do conector.
    escopos: {
      type: Array,
      default: () => [],
    },
    // Escopo da instância aberta, que é a origem da cópia.
    escopoAtual: {
      type: String,
      default: '',
    },
    // Instâncias já existentes do mesmo conector.
    instancias: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selecionados: [],
      replicando: false,
    };
  },
  computed: {
    destinos() {
      return this.escopos
        .filter((escopo) => escopo.chave !== this.escopoAtual)
        .map((escopo) => ({
          chave: escopo.chave,
          nome: escopo.nome,
          configurada: this.instancias.some((instancia) => instancia.escopo === escopo.chave),
        }));
    },

    todosMarcados() {
      return this.selecionados.length === this.destinos.length;
    },

    rotuloAplicar() {
      const total = this.selecionados.length;

      if (!total) {
        return 'Aplicar';
      }

      return total === 1 ? 'Aplicar a 1 finalidade' : `Aplicar a ${total} finalidades`;
    },
  },
  methods: {
    alternarTodos() {
      this.selecionados = this.todosMarcados ? [] : this.destinos.map((destino) => destino.chave);
    },

    /*
     * Sobrescrever configuração já existente é destrutivo e silencioso — quem
     * marcou "todas" sem reparar pode apagar um servidor diferente configurado
     * de propósito para boletos. Por isso a confirmação nomeia quais serão
     * substituídas.
     */
    replicar() {
      const sobrescritas = this.destinos
        .filter((destino) => this.selecionados.includes(destino.chave) && destino.configurada)
        .map((destino) => destino.nome);

      if (!sobrescritas.length) {
        this.enviar();

        return;
      }

      this.$swal({
        title: 'Substituir configurações existentes?',
        text:
          `Estas finalidades já têm configuração própria e serão substituídas: ${sobrescritas.join(', ')}.`,
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Substituir',
      }).then((resultado) => {
        if (resultado.value) {
          this.enviar();
        }
      });
    },

    enviar() {
      this.replicando = true;

      post(`/admin/integracoes/${this.integracaoId}/replicar`, { escopos: this.selecionados })
        .then((resposta) => {
          this.selecionados = [];
          this.$emit('replicada');
          this.$swal({
            title: resposta.message,
            icon: 'success',
            timer: 2200,
            showConfirmButton: false,
          });
        })
        .catch((erro) => {
          this.$swal({
            title: 'Não foi possível aplicar',
            text: erro.message,
            icon: 'error',
          });
        })
        .finally(() => {
          this.replicando = false;
        });
    },
  },
};
</script>

<style scoped>
.explicacao {
  margin: 0 0 14px;
  font-size: 12px;
  color: #667085;
  line-height: 1.5;
}

.destinos {
  list-style: none;
  margin: 0 0 14px;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 6px;
}

.destino__rotulo {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 7px 10px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #344054;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.destino__rotulo:hover {
  border-color: #b9b7d1;
  background: #fcfcfd;
}

.destino__aviso,
.destino__novo {
  display: block;
  font-size: 11px;
  line-height: 1.4;
}

.destino__aviso {
  color: #b25e09;
}

.destino__novo {
  color: #98a2b3;
}

.acoes {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
</style>
